import Layout from "@/components/layout/Layout";
import React, { useState } from "react";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import Sidepanel from "@/components/layout/Sidepanel";
import Label from "@/components/elements/Label";
import { InertiaLink } from "@inertiajs/inertia-react";
import TextInput from "@/components/elements/TextInput";
import DashboardMenu from "@/pages/Dashboard/DashboardMenu";
import api from "@/services/api";

/**
 * NewColor Dashboard Component
 *
 * Provides an interface for creating new color records in the system.
 * Allows input of color properties including RAL codes, Trespa codes,
 * hex values, delivery time settings, and external visibility.
 *
 * @param {Object} props - Component props
 * @param {string} props.saveColorUrl - API endpoint for creating the new color
 *
 * @returns {JSX.Element} The rendered new color form page
 */
export default function NewUser({ saveColorUrl }) {
    // Initialize state with default values for new color
    const [color, setColor] = useState({
        ral: "123",
        trespa: "456",
        hex: "789",
        levertijd: 1,
        extern: 1,
    });
    const [error, setError] = useState([]);

    /**
     * Handle form submission to create new color
     * Sends POST request to create the color and redirects on success
     *
     * @param {Event} e - Form submit event
     */
    function saveColor(e) {
        e.preventDefault();
        api
            .post(saveColorUrl, color)
            .then((response) => {
                window.location.href = "/dashboard/colors";
            })
            .catch((error) => setError(error.response.data.errors));
    }

    /**
     * Update color state with new values from TextInput components
     * Used by TextInput components for text field updates
     *
     * @param {Object} newColor - Updated color object
     */
    function updateColor(newColor) {
        setColor(newColor);
    }

    /**
     * Handle checkbox changes for boolean properties
     * Updates levertijd and extern properties based on checkbox state
     *
     * @param {Event} event - Checkbox change event
     * @param {string} property - Property name being updated (unused but kept for consistency)
     */
    function updateColorBoolean(event, property) {
        const target = event.target;
        const value =
            target.type === "checkbox" ? target.checked : target.value;
        const name = target.name;

        let colorprop = { ...color };
        colorprop[name] = value;
        setColor(colorprop);
    }

    return (
        <Layout>
            <Sidepanel>
                <DashboardMenu active={"Kleuren"} />
            </Sidepanel>
            <Content>
                <ContentOptions small>
                    <Heading heading={"Nieuwe kleur"} />
                </ContentOptions>
                <div
                    className={`contentoptions ingaveoptions col-md-9 d-flex flex-column`}
                >
                    <Heading heading="Kleur">
                        <div>
                            <InertiaLink
                                href="/dashboard/colors"
                                className="btn-orange"
                            >
                                terug
                            </InertiaLink>
                            <a
                                name={"Opslaan"}
                                href="#"
                                onClick={saveColor}
                                className="btn-orange"
                            >
                                opslaan
                            </a>
                        </div>
                    </Heading>

                    <div className="details">
                        <div className="question mb-3">
                            <Label label="Ral" small />
                            <TextInput
                                properties={color}
                                property="ral"
                                updateProperties={updateColor}
                            />
                            {error && error.ral ? (
                                <label className="labelsmall text-danger">
                                    {error.ral[0]}
                                </label>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="question mb-3">
                            <Label label="Trespa" small />
                            <TextInput
                                properties={color}
                                property="trespa"
                                updateProperties={updateColor}
                            />
                            {error && error.trespa ? (
                                <label className="labelsmall text-danger">
                                    {error.trespa[0]}
                                </label>
                            ) : (
                                ""
                            )}
                        </div>
                        <div className="question mb-3">
                            <Label label="Hex" small />
                            <TextInput
                                properties={color}
                                property="hex"
                                updateProperties={updateColor}
                            />
                            {error && error.hex ? (
                                <label className="labelsmall text-danger">
                                    {error.hex[0]}
                                </label>
                            ) : (
                                ""
                            )}
                        </div>

                        <div className="question mb-3">
                            <Label label="Lange levertijd" small />
                            <input
                                name="levertijd"
                                className="ml-3"
                                type="checkbox"
                                checked={color.levertijd}
                                onChange={updateColorBoolean}
                            />
                        </div>

                        <div className="question mb-3">
                            <Label
                                label="KLeur zichtbaar voor alle klanten"
                                small
                            />
                            <input
                                name="extern"
                                className="ml-3"
                                type="checkbox"
                                checked={color.extern}
                                onChange={updateColorBoolean}
                            />
                        </div>
                    </div>
                </div>
            </Content>
        </Layout>
    );
}
